import {FC} from 'react';
import {useField} from 'formik';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import useShow, {TShow} from '../../../../../hooks/useShow';
import {RootState} from '../../../../../redux/store.type';
import {TSlot} from '../../../../../redux/timetable/timetable.type';
import {useTimetableDays} from '../../../hooks/useTimetableDays';
import {stopWatch} from '../../../../../assets/symbols/symbols/Symbols.css';
import SelectPopup from '../../../../../component/selectPopup/SelectPopup';
import AddSlotPopup from './AddSlotPopup';
import {filterSlotsByDay, formatSlotTime} from './slots.utils';

interface TimeSlotSelectPopupProps {
    show: TShow;
    name?: string;
    // Day to filter the hours list by. Provide it directly when the form has
    // no `dayIndex` field (e.g. the break popup); otherwise it is resolved
    // from the form's selected day.
    dayTimestamp?: number;
}

const TimeSlotSelectPopup: FC<TimeSlotSelectPopupProps> = ({show, name = 'tsid', dayTimestamp}) => {
    const {t} = useTranslation('timetable');
    const [field, , helpers] = useField<number | null>(name);
    const [dayField] = useField<number>('dayIndex');
    const slots = useSelector((state: RootState) => state.timetable.slots);
    const {conferenceDays} = useTimetableDays();
    const addShow = useShow();

    // Filter by the relevant date so the hours list only shows that day's
    // slots, and updates when the lecture's date is changed while editing.
    const selectedDayTimestamp = dayTimestamp ?? conferenceDays[dayField.value]?.timestamp;

    const items = filterSlotsByDay(slots, selectedDayTimestamp)
        .map((s: TSlot) => ({id: s.tsid, text: formatSlotTime(s.timeStart)}));
    const handleAdd = (tsid: number) => { helpers.setValue(tsid); };

    return (
        <>
            {!addShow.isOpen && (
                <SelectPopup
                    show={show}
                    title={t('selectSlotTitle')}
                    items={items}
                    selectedIds={field.value !== null ? [field.value] : []}
                    onConfirm={ids => helpers.setValue(ids[0] ?? null)}
                    selectedFirst
                    addAction={{text: t('addSlot'), emptyText: t('addNewSlot'), onClick: addShow.open}}
                    hideSearch
                    empty={{icon: stopWatch, text: t('noSlotsYet')}}
                />
            )}
            <AddSlotPopup show={addShow} dayTimestamp={selectedDayTimestamp ?? 0} onAdd={handleAdd} />
        </>
    );
};

export default TimeSlotSelectPopup;
